import React from 'react';

import { Field } from 'formik';

import { formatActivityDateTime, ROW_SELECT } from './organisationUserFormat';

import TextButton from 'CommonComponents/TextButton';

const LINK = '!tw-normal-case';

/**
 * The Formik field holding a project's staged role. Keys are prefixed so
 * Formik keeps them as object keys rather than array indexes.
 */
export const projectRoleField = (eventId) => `project_roles.p${eventId}`;

/**
 * { p<event_id>: role } for the member's projects, as the panel's form starts.
 */
export const projectRoleValues = (projects) => Object.fromEntries(projects.map((project) => [`p${project.event_id}`, project.role ?? '']));

/**
 * The member's projects in this organisation. A manager changes a project
 * role here and saves it with the panel; adding to and removing from a
 * project apply at once.
 */
export default function OrganisationUserProjects({ projects, roles, canManage, onAdd, onRemove }) {
    return (
        <section className="tw-space-y-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-charcoal">{$t('Projects')}</h3>
                {canManage && (
                    <TextButton variant="primary" className={LINK} onClick={onAdd}>
                        + {$t('Add to project')}
                    </TextButton>
                )}
            </div>

            {projects.length === 0 ? (
                <p className="tw-mb-0 tw-text-sm tw-text-charcoal-50">{$t('Not on any project in this organisation.')}</p>
            ) : (
                <table className="tw-w-full tw-text-0.5sm">
                    <thead>
                        <tr className="tw-border-b tw-border-grey tw-text-xs tw-text-charcoal-75">
                            <th className="tw-py-2 tw-pe-3 tw-text-start tw-font-medium">{$t('Project')}</th>
                            <th className="tw-py-2 tw-pe-3 tw-text-start tw-font-medium">{$t('Project role')}</th>
                            <th className="tw-py-2 tw-pe-3 tw-text-start tw-font-medium">{$t('Last login')}</th>
                            {canManage && (
                                <th className="tw-py-2 tw-text-end tw-font-medium">
                                    <span className="tw-sr-only">{$t('Actions')}</span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map((project) => (
                            <tr key={project.event_id} className="tw-border-b tw-border-grey last:tw-border-b-0">
                                <td className="tw-py-2 tw-pe-3 tw-text-charcoal">{project.name}</td>
                                <td className="tw-py-2 tw-pe-3 tw-text-charcoal">
                                    {canManage ? (
                                        <Field
                                            as="select"
                                            name={projectRoleField(project.event_id)}
                                            aria-label={$t('Project role on {{project}}', { project: project.name })}
                                            className={`select-input ${ROW_SELECT}`}
                                        >
                                            {!project.role && <option value="">—</option>}
                                            {/* A role the plan no longer offers stays selectable for the member who holds it. */}
                                            {[...roles, ...(project.role && !roles.includes(project.role) ? [project.role] : [])].map((name) => (
                                                <option key={name} value={name}>
                                                    {$t(name)}
                                                </option>
                                            ))}
                                        </Field>
                                    ) : project.role ? (
                                        $t(project.role)
                                    ) : (
                                        <span className="tw-text-charcoal-50">—</span>
                                    )}
                                </td>
                                <td className="tw-py-2 tw-pe-3 tw-text-charcoal-50">
                                    {project.last_login_at ? formatActivityDateTime(project.last_login_at) : $t('Never')}
                                </td>
                                {canManage && (
                                    <td className="tw-py-2 tw-text-end">
                                        <TextButton variant="danger" className={LINK} onClick={() => onRemove(project)}>
                                            {$t('Remove')}
                                        </TextButton>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <p className="tw-mb-0 tw-rounded-lg tw-bg-blue-15 tw-px-4 tw-py-3 tw-text-xs tw-text-charcoal-85">
                {$t(
                    'Departments, venues, cost centres and modules are set inside each project by an Event Admin. Organisation level sets project membership and project role only.',
                )}
            </p>
        </section>
    );
}
